import React, { useEffect, useState } from "react";
import {
  CircularProgress,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Avatar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SchoolIcon from "@mui/icons-material/School";
import { useSnackbar } from "notistack";
import {
  get_school_instructors_api,
  update_instructor_profile_api,
} from "../../DAL/Tutors/Tutors";

const inputStyles = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    borderRadius: "12px",
    "& fieldset": { borderColor: "rgba(255, 255, 255, 0.1)" },
    "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.2)" },
    "&.Mui-focused fieldset": { borderColor: "#c01e2e" },
  },
  "& .MuiInputBase-input": { color: "#e2e8f0" },
  "& .MuiInputLabel-root": { color: "#94a3b8" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#c01e2e" },
};

export default function FlightSchoolTutors() {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [instructors, setInstructors] = useState([]);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editInstructor, setEditInstructor] = useState(null);
  const [editForm, setEditForm] = useState({ bio: "", photoUrl: "", subjects: [], isBookable: true });
  const [subjectInput, setSubjectInput] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);

  useEffect(() => {
    loadInstructors();
  }, []);

  const loadInstructors = async () => {
    try {
      const result = await get_school_instructors_api();
      if (result.code === 200) {
        setInstructors(result.data || []);
      } else {
        enqueueSnackbar(result.message || "Failed to load instructors", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("Error loading instructors", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const openEdit = (instructor) => {
    setEditInstructor(instructor);
    setEditForm({
      bio: instructor.tutoring?.bio || "",
      photoUrl: instructor.tutoring?.photoUrl || "",
      subjects: instructor.tutoring?.subjects || [],
      isBookable: instructor.tutoring?.isBookable ?? true,
    });
    setSubjectInput("");
    setEditDialogOpen(true);
  };

  const addSubject = () => {
    const value = subjectInput.trim();
    if (!value || editForm.subjects.includes(value)) {
      setSubjectInput("");
      return;
    }
    setEditForm({ ...editForm, subjects: [...editForm.subjects, value] });
    setSubjectInput("");
  };

  const removeSubject = (subject) => {
    setEditForm({ ...editForm, subjects: editForm.subjects.filter((s) => s !== subject) });
  };

  const handleSave = async () => {
    setEditSubmitting(true);
    try {
      const result = await update_instructor_profile_api(editInstructor._id, editForm);
      if (result.code === 200) {
        enqueueSnackbar("Instructor profile updated", { variant: "success" });
        setEditDialogOpen(false);
        setEditInstructor(null);
        loadInstructors();
      } else {
        enqueueSnackbar(result.message || "Failed to update profile", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("Error updating profile", { variant: "error" });
    } finally {
      setEditSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography sx={{ color: "#e2e8f0", fontSize: "16px", fontWeight: 500 }}>
          Tutoring Instructors
        </Typography>
      </Box>

      <Paper
        sx={{
          p: 2,
          mb: 3,
          backgroundColor: "rgba(33,150,243,0.1)",
          border: "1px solid rgba(33,150,243,0.2)",
          borderRadius: "12px",
        }}
      >
        <Typography sx={{ color: "#2196f3", fontSize: "13px" }}>
          Edit an instructor's public tutoring profile — bio, subjects, photo, and whether they
          appear as bookable to students. Rates and working hours are managed by the instructor
          themselves (or under Settings, depending on this school's rate mode).
        </Typography>
      </Paper>

      <TableContainer component={Paper} sx={{ backgroundColor: "transparent", boxShadow: "none" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: "#c01e2e", fontWeight: 600, borderBottom: "1px solid rgba(255,255,255,0.1)" }}>Instructor</TableCell>
              <TableCell sx={{ color: "#c01e2e", fontWeight: 600, borderBottom: "1px solid rgba(255,255,255,0.1)" }}>Subjects</TableCell>
              <TableCell sx={{ color: "#c01e2e", fontWeight: 600, borderBottom: "1px solid rgba(255,255,255,0.1)" }}>Bio</TableCell>
              <TableCell sx={{ color: "#c01e2e", fontWeight: 600, borderBottom: "1px solid rgba(255,255,255,0.1)" }}>Bookable</TableCell>
              <TableCell sx={{ color: "#c01e2e", fontWeight: 600, borderBottom: "1px solid rgba(255,255,255,0.1)", width: 60 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {instructors.map((instructor) => (
              <TableRow key={instructor._id}>
                <TableCell sx={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar src={instructor.tutoring?.photoUrl || undefined} sx={{ width: 36, height: 36, backgroundColor: "#c01e2e" }}>
                      {instructor.first_name?.[0]}{instructor.last_name?.[0]}
                    </Avatar>
                    <Box>
                      <Typography sx={{ color: "#e2e8f0", fontSize: "14px" }}>
                        {instructor.first_name} {instructor.last_name}
                      </Typography>
                      <Typography sx={{ color: "#64748b", fontSize: "11px" }}>{instructor.email}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ borderBottom: "1px solid rgba(255,255,255,0.05)", maxWidth: 220 }}>
                  {(instructor.tutoring?.subjects || []).length === 0 ? (
                    <Typography sx={{ color: "#64748b", fontSize: "12px" }}>—</Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {instructor.tutoring.subjects.map((s) => (
                        <Chip key={s} label={s.replace(/_/g, " ")} size="small" sx={{ backgroundColor: "rgba(192,30,46,0.15)", color: "#c01e2e", fontSize: "11px" }} />
                      ))}
                    </Box>
                  )}
                </TableCell>
                <TableCell sx={{ color: "#94a3b8", fontSize: "13px", borderBottom: "1px solid rgba(255,255,255,0.05)", maxWidth: 260 }}>
                  <Typography sx={{ color: "#94a3b8", fontSize: "13px", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                    {instructor.tutoring?.bio || "—"}
                  </Typography>
                </TableCell>
                <TableCell sx={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                  <Chip
                    label={instructor.tutoring?.isBookable ? "Bookable" : "Hidden"}
                    size="small"
                    sx={{
                      backgroundColor: instructor.tutoring?.isBookable ? "rgba(76,175,80,0.2)" : "rgba(255,255,255,0.08)",
                      color: instructor.tutoring?.isBookable ? "#4caf50" : "#94a3b8",
                      fontSize: "11px",
                    }}
                  />
                </TableCell>
                <TableCell sx={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                  <IconButton size="small" onClick={() => openEdit(instructor)} sx={{ color: "#94a3b8" }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {instructors.length === 0 && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <SchoolIcon sx={{ fontSize: 48, color: "#3f3f46", mb: 2 }} />
          <Typography sx={{ color: "#64748b" }}>No instructors at this school yet</Typography>
        </Box>
      )}

      {/* Edit Profile Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#1a1a1a",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "16px",
          },
        }}
      >
        <DialogTitle sx={{ color: "#e2e8f0" }}>
          Edit Profile — {editInstructor?.first_name} {editInstructor?.last_name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar src={editForm.photoUrl || undefined} sx={{ width: 56, height: 56, backgroundColor: "#c01e2e" }}>
                {editInstructor?.first_name?.[0]}{editInstructor?.last_name?.[0]}
              </Avatar>
              <TextField
                fullWidth
                label="Photo URL"
                value={editForm.photoUrl}
                onChange={(e) => setEditForm({ ...editForm, photoUrl: e.target.value })}
                sx={inputStyles}
              />
            </Box>

            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Bio"
              value={editForm.bio}
              onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
              sx={inputStyles}
            />

            <Box>
              <Typography sx={{ color: "#c01e2e", fontSize: "13px", fontWeight: 600, mb: 1 }}>
                SUBJECTS
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
                {editForm.subjects.length === 0 && (
                  <Typography sx={{ color: "#64748b", fontSize: "12px" }}>No subjects added yet</Typography>
                )}
                {editForm.subjects.map((s) => (
                  <Chip
                    key={s}
                    label={s.replace(/_/g, " ")}
                    size="small"
                    onDelete={() => removeSubject(s)}
                    sx={{ backgroundColor: "rgba(192,30,46,0.15)", color: "#c01e2e", fontSize: "11px" }}
                  />
                ))}
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g. ppl_navigation"
                  value={subjectInput}
                  onChange={(e) => setSubjectInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addSubject();
                    }
                  }}
                  sx={inputStyles}
                />
                <Button
                  variant="outlined"
                  onClick={addSubject}
                  sx={{ borderColor: "rgba(255,255,255,0.2)", color: "#94a3b8", "&:hover": { borderColor: "#c01e2e", color: "#c01e2e" } }}
                >
                  Add
                </Button>
              </Box>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={editForm.isBookable}
                  onChange={(e) => setEditForm({ ...editForm, isBookable: e.target.checked })}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#c01e2e" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#c01e2e" },
                  }}
                />
              }
              label={
                <Box>
                  <Typography sx={{ color: "#e2e8f0", fontSize: "14px" }}>Bookable</Typography>
                  <Typography sx={{ color: "#94a3b8", fontSize: "12px" }}>
                    Whether students can find and book this instructor
                  </Typography>
                </Box>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ color: "#94a3b8" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={editSubmitting}
            sx={{ backgroundColor: "#c01e2e", "&:hover": { backgroundColor: "#a01828" } }}
          >
            {editSubmitting ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Save Profile"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
